import L from "leaflet";
import { IOT_STATUS_CONFIG } from "../../config/iotConnectionStatusConfig";
import { getCountLabelFontSize, getDonutSize } from "../../utils/clusterMarkerLayout";
import { formatCompactCount } from "../../utils/formatCompactCount";

interface StatusCount {
    status: string;
    count: number;
}

const STATUS_ORDER = IOT_STATUS_CONFIG.map((c) => c.status);

export function createDonutIcon(
    statusCounts: StatusCount[],
    totalCount: number,
    colorMap: Record<string, string>,
    locale: string = "en"
): L.DivIcon {
    const size = getDonutSize(totalCount);
    const half = size / 2;
    const strokeWidth = size * 0.2;
    const radius = (size - strokeWidth) / 2;
    const circumference = 2 * Math.PI * radius;

    const sorted = STATUS_ORDER.map((s) =>
        statusCounts.find((sc) => sc.status === s)
    ).filter((sc): sc is StatusCount => sc != null && sc.count > 0);

    const statusTotal = sorted.reduce((sum, sc) => sum + sc.count, 0);
    const unaccounted = totalCount - statusTotal;

    let circles = "";
    let offset = 0;

    for (const sc of sorted) {
        const fraction = sc.count / totalCount;
        const dashLength = fraction * circumference;
        const color = colorMap[sc.status] || "#999";
        circles += `<circle
      cx="${half}" cy="${half}" r="${radius}"
      fill="none"
      stroke="${color}"
      stroke-width="${strokeWidth}"
      stroke-dasharray="${dashLength} ${circumference - dashLength}"
      stroke-dashoffset="${-offset}"
      transform="rotate(-90 ${half} ${half})"
    />`;
        offset += dashLength;
    }

    if (unaccounted > 0) {
        const fraction = unaccounted / totalCount;
        const dashLength = fraction * circumference;
        circles += `<circle
      cx="${half}" cy="${half}" r="${radius}"
      fill="none"
      stroke="#c0c0c0"
      stroke-width="${strokeWidth}"
      stroke-dasharray="${dashLength} ${circumference - dashLength}"
      stroke-dashoffset="${-offset}"
      transform="rotate(-90 ${half} ${half})"
    />`;
    }

    // Compact label ("1.2k", "28k") so five-digit counts no longer overflow the inner disc.
    const label = formatCompactCount(totalCount, locale);
    const fontSize = getCountLabelFontSize(size, label.length);

    let innerFill = "white";
    let textFill = "#333";

    const centerStatus = IOT_STATUS_CONFIG.find((cfg) =>
        sorted.some((sc) => sc.status === cfg.status)
    );
    if (centerStatus) {
        innerFill = centerStatus.color;
        textFill = centerStatus.centerTextColor;
    }

    const svg = `
    <svg width="${size}" height="${size}" viewBox="0 0 ${size} ${size}"
         xmlns="http://www.w3.org/2000/svg">
      <circle cx="${half}" cy="${half}" r="${radius - strokeWidth / 2}"
              fill="${innerFill}" />
      ${circles}
      <text x="${half}" y="${half}" text-anchor="middle"
            dominant-baseline="central"
            font-size="${fontSize}" font-weight="bold" fill="${textFill}"
            font-family="sans-serif">
        ${label}
      </text>
    </svg>`;

    return L.divIcon({
        html: svg,
        className: "donut-cluster-marker",
        iconSize: [size, size],
        iconAnchor: [half, half],
    });
}
